import React, { useMemo, useState, useEffect } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { Option } from "react-bootstrap-typeahead/types/types";
import styled from "styled-components";
import {
  Sector,
  useLeerCiudadOrigen,
  useLeerSectoresOrigen,
} from "../../../../services/paisesServicios";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { HeaderSection } from "../HeaderSection";
import { Title } from "../Title";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AirportShuttleIcon from "@mui/icons-material/AirportShuttle";
import { Model } from "../Model";
import CalculoPrecioTransporte from "../CalculoPrecioTransporte/CalculoPrecioTransporte";
import { useWizard } from "../../../../hooks/wizard";
import Fechas from "../Fechas/Fechas";
import SuccessButton from "../../../organisms/SuccessButton";
import { usePasoReservaHotel } from "../pasoReservaHotel";

function DatosTransporte({ model }: { model: Model }) {
  const datosPropietario = model.datosPropietario!!;
  const {
    currentModel,
    definirDireccionTransporte,
    definirSectorTransporte,
    definirContrataIda,
    definirContrataVuelta,
  } = usePasoReservaHotel(model);
  const { avanza, vuelve, volver, avanzar } = useWizard();
  const [usarDireccionResidencia, setUsarDireccionResidencia] =
    useState<boolean>(definirUsaTransporteResidenciaInicial(model));

  const [sectorTransporteInicial, setSectorTransporteInicial] =
    useState<Option | null>(obtenerSectorInicial(model));

  const {
    data: sectoresOrigen,
    isSuccess: sectoresIsSuccess,
    error: errorSectoresOrigen,
    isLoading: sectoresOrigenIsLoading,
  } = useLeerSectoresOrigen();

  const { data: ciudadOrigen, error: errorCiudadOrigen } =
    useLeerCiudadOrigen();

  const sectoresOptions: Option[] = useMemo(() => {
    if (sectoresIsSuccess && sectoresOrigen) {
      return sectoresOrigen.map((s) => ({ id: s.id, nombre: s.nombre }));
    }
    return [];
  }, [sectoresIsSuccess, sectoresOrigen]);

  useEffect(() => {
    if (
      datosResidenciaSonValidosParaTransporte(model) &&
      !model.direccionTransporte
    ) {
      definirDireccionTransporte({
        direccion: model.datosPropietario!!.direccionResidencia!!,
      });
      definirSectorTransporte({
        sector: model.datosPropietario!!.sectorResidencia!!,
      });
    }
  }, [model, definirDireccionTransporte, definirSectorTransporte]);

  const onUsarDirreccionResidenciaChange = () => {
    const usara = !usarDireccionResidencia;
    if (usara) {
      if (datosResidenciaSonValidosParaTransporte(model)) {
        definirDireccionTransporte({
          direccion: datosPropietario.direccionResidencia!!,
        });
        definirSectorTransporte({
          sector: datosPropietario.sectorResidencia!!,
        });
        setSectorTransporteInicial({
          id: datosPropietario.sectorResidencia!!.id,
          nombre: datosPropietario.sectorResidencia!!!.nombre,
        } as Option);
      }
    } else {
      definirDireccionTransporte({
        direccion: undefined,
      });
      definirSectorTransporte({
        sector: undefined,
      });
      setSectorTransporteInicial(null);
    }
    setUsarDireccionResidencia(usara);
  };

  const puedeAvanzar =
    !!currentModel.direccionTransporte && !!currentModel.sectorTransporte;

  const error = errorSectoresOrigen || errorCiudadOrigen;

  const onIdaChange = () => {
    definirContrataIda({ contrata: !currentModel.contrataIda });
  };

  const onVueltaChange = () => {
    definirContrataVuelta({ contrata: !currentModel.contrataVuelta });
  };

  const onDireccionTransporteChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    definirDireccionTransporte({ direccion: event.target.value });
  };

  const onSectorTransporteChange = (selected: Option[]) => {
    if (selected.length === 1) {
      const rec = selected[0] as Record<string, any>;
      const nuevoSector: Sector = {
        id: rec.id,
        nombre: rec.nombre,
      };
      definirSectorTransporte({ sector: nuevoSector });
    }
  };

  if (error) {
    return <ServerErrorModal />;
  }

  if (avanza) {
    return <CalculoPrecioTransporte model={currentModel} />;
  }

  if (vuelve) {
    return <Fechas model={currentModel} />;
  }

  return (
    <Row className="justify-content-center">
      <Col className="col-10">
        <HeaderSection className="pt-4 pb-4">
          <Title>
            Contratación de transporte{" "}
            <IconContainer>
              <AirportShuttleIcon sx={{ fontSize: 30 }} />
            </IconContainer>
          </Title>
        </HeaderSection>
        <Row>
          <section className="mb-3">
            <h5>Datos de residencia</h5>
            <div className="mt-3">
              <label className="mb-2">
                <strong>Propietario: </strong>{" "}
                <NombrePropietario
                  primerNombre={datosPropietario.primerNombre}
                  primerApellido={datosPropietario.primerApellido}
                />
              </label>
            </div>
            <div>
              <label className="mb-2">
                <strong>Ciudad de residencia: </strong>{" "}
                <>{datosPropietario.ciudadResidencia?.nombre}</>
              </label>
            </div>
            <div>
              <label className="mb-2">
                <strong>Sector de residencia: </strong>{" "}
                <>{datosPropietario.sectorResidencia?.nombre}</>
              </label>
            </div>
            <div>
              <label className="mb-2">
                <strong>Dirección de residencia: </strong>{" "}
                <>{datosPropietario.direccionResidencia}</>
              </label>
            </div>
            <hr></hr>
          </section>
          <section>
            <h5>Opciones</h5>
            {datosResidenciaSonValidosParaTransporte(model) && (
              <div className="mb-4 mt-3">
                <label className="mb-1">
                  Transportar a dirección de residencia
                </label>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="flexSwitchCheckDefault"
                    checked={usarDireccionResidencia}
                    onChange={onUsarDirreccionResidenciaChange}
                  />
                  <label>{usarDireccionResidencia ? "Si" : "No"} </label>
                </div>
              </div>
            )}

            {!usarDireccionResidencia && (
              <div className="mb-4">
                {ciudadOrigen && (
                  <p>
                    <i>
                      El transporte se realiza en la ciudad de
                      {" " + ciudadOrigen.nombre}
                    </i>
                  </p>
                )}
                <div className="mb-2">
                  <label
                    className="form-label"
                    htmlFor="direccion-transporte-input"
                  >
                    Dirección de transporte:
                  </label>
                  <input
                    id="direccion-transporte-input"
                    type="text"
                    value={currentModel.direccionTransporte}
                    className="form-control"
                    onChange={onDireccionTransporteChange}
                  ></input>
                </div>
                <div className="mb-2">
                  <label
                    className="form-label"
                    htmlFor="sector-transporte-input"
                  >
                    Sector de transporte:
                  </label>
                  {sectorTransporteInicial ? (
                    <Typeahead
                      defaultSelected={[sectorTransporteInicial]}
                      clearButton
                      id="input-sector"
                      options={[sectorTransporteInicial]}
                      labelKey="nombre"
                      onChange={onSectorTransporteChange}
                      isLoading={sectoresOrigenIsLoading}
                    />
                  ) : (
                    <Typeahead
                      clearButton
                      id="input-sector"
                      options={sectoresOptions}
                      labelKey="nombre"
                      onChange={onSectorTransporteChange}
                      isLoading={sectoresOrigenIsLoading}
                    />
                  )}
                </div>
              </div>
            )}
            <div className="mb-2">
              <label>
                Ida:
                <StyledCheck
                  type="checkbox"
                  className="form-check-input ms-2"
                  checked={currentModel.contrataIda}
                  onChange={onIdaChange}
                />
              </label>
            </div>
            <div className="mb-2">
              <label>
                Vuelta:
                <StyledCheck
                  type="checkbox"
                  className="form-check-input ms-2"
                  checked={currentModel.contrataVuelta}
                  onChange={onVueltaChange}
                />
              </label>
            </div>
          </section>
          <StyledButtonRow className="mb-2 mt-3 justify-content-between">
            <Button
              type={"button"}
              style={{ width: "fit-content" }}
              onClick={volver}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
              Volver
            </Button>
            <SuccessButton
              type={"button"}
              style={{ width: "fit-content" }}
              disabled={!puedeAvanzar}
              onClick={avanzar}
            >
              Siguiente <ArrowForwardIcon sx={{ fontSize: 20 }} />
            </SuccessButton>
          </StyledButtonRow>
        </Row>
      </Col>
    </Row>
  );
}

const NombrePropietario = ({
  primerNombre,
  primerApellido,
}: {
  primerNombre: string;
  primerApellido: string;
}) => {
  return <>{primerNombre + " " + primerApellido}</>;
};

const StyledCheck = styled("input")`
  display: inline-block;
  vertical-align: middle;
  margin-top: 0;
`;

const StyledButtonRow = styled(Row)`
  padding-right: 0;
`;

const IconContainer = styled("span")`
  display: relative;
  color: ${(props) => props.theme.colors.main};
`;

const direccionTransporteEsResidencia = (model: Model): boolean => {
  return (
    model.sectorTransporte !== undefined &&
    model.direccionTransporte !== undefined &&
    model.datosPropietario !== undefined &&
    model.datosPropietario.direccionResidencia !== undefined &&
    model.datosPropietario.sectorResidencia !== undefined &&
    model.datosPropietario.direccionResidencia === model.direccionTransporte &&
    model.datosPropietario.sectorResidencia.id === model.sectorTransporte.id
  );
};

const definirUsaTransporteResidenciaInicial = (model: Model): boolean => {
  if (model.sectorTransporte && model.direccionTransporte) {
    return direccionTransporteEsResidencia(model);
  }
  return datosResidenciaSonValidosParaTransporte(model);
};

const obtenerSectorInicial = (model: Model): Option | null => {
  if (model.sectorTransporte !== undefined) {
    return {
      id: model.sectorTransporte!.id,
      nombre: model.sectorTransporte!.nombre,
    } as Option;
  }
  const datosPropietario = model.datosPropietario!!;
  if (datosResidenciaSonValidosParaTransporte(model)) {
    return {
      id: datosPropietario.sectorResidencia!!.id,
      nombre: datosPropietario.sectorResidencia!!.nombre,
    } as Option;
  }
  return null;
};

const datosResidenciaSonValidosParaTransporte = (model: Model): boolean => {
  const datosPropietario = model.datosPropietario!!;
  const ciudadOrigen = model.ciudadOrigen!!;

  return (
    !!datosPropietario.direccionResidencia &&
    !!datosPropietario.sectorResidencia &&
    !!ciudadOrigen &&
    !!datosPropietario.ciudadResidencia &&
    !!datosPropietario.ciudadResidencia.id === !!ciudadOrigen.id
  );
};

export default DatosTransporte;
